import React, { forwardRef } from 'react';
import { OrgSelector } from './OrgSelector';
import { OrgUnitSelector } from './OrgUnitSelector';
import { ORG_ORG_UNIT_SELECTOR_MODE } from '../../constants/org-org-unit-selector-modes';
import _ from 'lodash';
// eslint-disable-next-line unused-imports/no-unused-imports -- referenced by the props typedef
import SelectionList from '../SelectionList';

/**
 * @typedef {object} OrgOrgUnitSelectorsOrgSelectorOwnProps
 * @property {number|Array<*>} [value]
 * @property {(selectedOrgIds: number | Array<number>) => void} [onChange]
 */

/**
 * @typedef {OrgOrgUnitSelectorsOrgSelectorOwnProps &
 *   React.ComponentPropsWithoutRef<typeof SelectionList>} OrgOrgUnitSelectorsOrgSelectorProps
 */

/**
 * @typedef {object} OrgOrgUnitSelectorsOrgUnitSelectorOwnProps
 * @property {Array<*>} [value]
 * @property {(orgUnitIds: Array<number>) => void} [onChange]
 */

/**
 * @typedef {OrgOrgUnitSelectorsOrgUnitSelectorOwnProps &
 *   React.ComponentPropsWithoutRef<typeof SelectionList>} OrgOrgUnitSelectorsOrgUnitSelectorProps
 */

/**
 * @typedef {object} OrgOrgUnitSelectorsProps
 * @property {Array<*>} organizations
 * @property {string} [sectionClassName]
 * @property {number} [selectorMode]
 * @property {boolean} [disabled]
 * @property {boolean} [loading]
 * @property {OrgOrgUnitSelectorsOrgSelectorProps} orgSelectorProps
 * @property {OrgOrgUnitSelectorsOrgUnitSelectorProps} orgUnitSelectorProps
 */

/**
 * @type {React.ForwardRefExoticComponent<OrgOrgUnitSelectorsProps & React.RefAttributes<*>>}
 * @param {OrgOrgUnitSelectorsProps} props
 */
export const OrgOrgUnitSelectors = forwardRef((props, _ref) => {
  const {
    organizations,
    sectionClassName,
    disabled = false,
    loading = false,
    selectorMode = ORG_ORG_UNIT_SELECTOR_MODE.MULTIPLE_ORG_MULTIPLE_ORG_UNIT_SELECTION,
    orgSelectorProps,
    orgUnitSelectorProps
  } = props;

  const isSingleOrgSelect =
    selectorMode === ORG_ORG_UNIT_SELECTOR_MODE.SINGLE_ORG_MULTIPLE_UNIT_SELECTION ||
    selectorMode === ORG_ORG_UNIT_SELECTOR_MODE.SINGLE_ORG_SINGLE_UNIT_SELECTION;

  const selectedOrgsInner = _.isArray(orgSelectorProps.value)
    ? orgSelectorProps.value
    : [orgSelectorProps.value];

  const handleOrganizationsChange = selectedOrgIds => {
    orgSelectorProps.onChange(isSingleOrgSelect ? selectedOrgIds[0] : selectedOrgIds);
    orgUnitSelectorProps.onChange(cleanupSelectedOrgUnits(selectedOrgIds));
  };

  const cleanupSelectedOrgUnits = selectedOrgIds => {
    if (selectorMode === ORG_ORG_UNIT_SELECTOR_MODE.SINGLE_ORG_SINGLE_UNIT_SELECTION) {
      return null;
    }

    //Deselect Org Units from deselected Organization
    const unitsBySelectedOrganizations = organizations
      .filter(org => !selectedOrgIds || selectedOrgIds.includes(org.Id))
      .flatMap(org => org.MddapOrgUnits.map(ou => ou.Id));

    return orgUnitSelectorProps.value
      ? orgUnitSelectorProps.value?.filter(orgUnitId =>
          unitsBySelectedOrganizations.includes(orgUnitId)
        )
      : unitsBySelectedOrganizations;
  };

  return (
    <>
      <div className={sectionClassName}>
        <OrgSelector
          {...orgSelectorProps}
          organizations={organizations}
          value={selectedOrgsInner}
          onChange={handleOrganizationsChange}
          isSingleOrgSelect={isSingleOrgSelect}
          disabled={disabled}
          loading={loading}
        />
      </div>
      <div className={sectionClassName}>
        <OrgUnitSelector
          {...orgUnitSelectorProps}
          organizations={organizations.filter(org => selectedOrgsInner.includes(org.Id))}
          multiSelect={selectorMode !== ORG_ORG_UNIT_SELECTOR_MODE.SINGLE_ORG_SINGLE_UNIT_SELECTION}
          disabled={disabled}
          loading={loading}
        />
      </div>
    </>
  );
});

OrgOrgUnitSelectors.displayName = 'OrgOrgUnitSelectors';
